import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import {
  ArrowLeft,
  Wine,
  Search,
  Ticket,
  CheckCircle2,
  Clock,
  Wallet,
} from "lucide-react";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/harikaradmin/monitor/$eventId")({
  head: () => ({ meta: [{ title: "Etkinlik İzleme — JUËN" }] }),
  component: AdminMonitorPage,
});

interface VoucherRow {
  id: string;
  user_id: string;
  redeemed_at: string | null;
  created_at: string;
  drinks: { id: string; name: string } | null;
}

interface ProfileRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  photo_url: string | null;
}

interface EventRow {
  id: string;
  name: string;
}

interface TicketRow {
  id: string;
  user_id: string;
  code: string;
  checked_in_at: string | null;
  created_at: string;
  ticket_types: { name: string } | null;
}

type TabKey = "tickets" | "vouchers";

function AdminMonitorPage() {
  const { eventId } = Route.useParams();
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [allVouchers, setAllVouchers] = useState<VoucherRow[]>([]);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [profiles, setProfiles] = useState<Map<string, ProfileRow>>(new Map());
  const [pageLoading, setPageLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabKey>("tickets");

  useEffect(() => {
    if (!loading && !isAdmin) void navigate({ to: "/dashboard" });
  }, [loading, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const fetchData = async () => {
      const [{ data: ev }, { data: all }, { data: tk }] = await Promise.all([
        supabase.from("events").select("id,name").eq("id", eventId).maybeSingle(),
        supabase
          .from("drink_vouchers")
          .select("id,user_id,redeemed_at,created_at,drinks(id,name)")
          .eq("event_id", eventId),
        supabase
          .from("tickets")
          .select("id,user_id,code,checked_in_at,created_at,ticket_types(name)")
          .eq("event_id", eventId)
          .order("created_at", { ascending: false }),
      ]);
      if (cancelled) return;
      setEvent((ev as EventRow | null) ?? null);
      const allRows = (all as unknown as VoucherRow[]) ?? [];
      const ticketRows = (tk as unknown as TicketRow[]) ?? [];
      setAllVouchers(allRows);
      setTickets(ticketRows);

      const userIds = Array.from(
        new Set([
          ...allRows.map((r) => r.user_id),
          ...ticketRows.map((r) => r.user_id),
        ]),
      );
      if (userIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id,first_name,last_name,email,photo_url")
          .in("id", userIds);
        if (cancelled) return;
        const map = new Map<string, ProfileRow>();
        for (const p of (profs as ProfileRow[]) ?? []) map.set(p.id, p);
        setProfiles(map);
      } else {
        setProfiles(new Map());
      }
      setPageLoading(false);
    };
    void fetchData();
    const refetch = setInterval(() => void fetchData(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(refetch);
    };
  }, [eventId, isAdmin]);

  const filteredTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter((t) => {
      const p = profiles.get(t.user_id);
      const name = `${p?.first_name ?? ""} ${p?.last_name ?? ""}`.toLowerCase();
      return (
        name.includes(q) ||
        (p?.email ?? "").toLowerCase().includes(q) ||
        t.code.toLowerCase().includes(q)
      );
    });
  }, [tickets, profiles, search]);

  const checkedIn = tickets.filter((t) => t.checked_in_at).length;
  const totalTickets = tickets.length;

  // Voucher summary by drink
  const voucherSummary = useMemo(() => {
    const byDrink = new Map<string, { name: string; sold: number; redeemed: number }>();
    for (const v of allVouchers) {
      const key = v.drinks?.id ?? "unknown";
      const name = v.drinks?.name ?? "İçecek";
      if (!byDrink.has(key)) byDrink.set(key, { name, sold: 0, redeemed: 0 });
      const b = byDrink.get(key)!;
      b.sold += 1;
      if (v.redeemed_at) b.redeemed += 1;
    }
    return Array.from(byDrink.values()).sort((a, b) => b.sold - a.sold);
  }, [allVouchers]);

  const totalSoldVouchers = allVouchers.length;
  const totalRedeemedVouchers = allVouchers.filter((v) => v.redeemed_at).length;

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <AdminTabs />
        <Link to="/harikaradmin/events" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Etkinlikler
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Etkinlik İzleme</h1>
            <p className="mt-1 text-muted-foreground">{event?.name ?? "Etkinlik"}</p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="mt-6 flex gap-2 rounded-xl border border-border bg-card p-1 shadow-card">
          <TabBtn active={tab === "tickets"} onClick={() => setTab("tickets")} icon={<Ticket className="h-4 w-4" />} label={`Biletler (${checkedIn}/${totalTickets})`} />
          <TabBtn active={tab === "vouchers"} onClick={() => setTab("vouchers")} icon={<Wallet className="h-4 w-4" />} label={`Kuponlar (${totalRedeemedVouchers}/${totalSoldVouchers})`} />
        </div>

        {/* Search */}
        {tab === "tickets" && (
          <div className="mt-4 relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Üye veya bilet kodu ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {pageLoading ? (
          <div className="mt-8 text-center text-muted-foreground">Veriler yükleniyor...</div>
        ) : tab === "tickets" ? (
          <TicketsTab tickets={filteredTickets} totalCount={tickets.length} checkedIn={checkedIn} profiles={profiles} />
        ) : (
          <VouchersTab summary={voucherSummary} totalSold={totalSoldVouchers} totalRedeemed={totalRedeemedVouchers} />
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function TicketsTab({
  tickets,
  totalCount,
  checkedIn,
  profiles,
}: {
  tickets: TicketRow[];
  totalCount: number;
  checkedIn: number;
  profiles: Map<string, ProfileRow>;
}) {
  const pending = totalCount - checkedIn;
  return (
    <>
      <div className="mt-6 grid gap-3 md:grid-cols-3">
        <SummaryTile label="Toplam bilet" value={totalCount.toString()} icon={<Ticket className="h-5 w-5" />} tone="default" />
        <SummaryTile label="Giriş yapan" value={checkedIn.toString()} icon={<CheckCircle2 className="h-5 w-5" />} tone="success" />
        <SummaryTile label="Bekleyen" value={pending.toString()} icon={<Clock className="h-5 w-5" />} tone={pending > 0 ? "warning" : "default"} />
      </div>

      {tickets.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          Bilet bulunamadı.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {tickets.map((t) => {
            const p = profiles.get(t.user_id);
            const fullName = `${p?.first_name ?? ""} ${p?.last_name ?? ""}`.trim() || p?.email || "Üye";
            const checked = !!t.checked_in_at;
            return (
              <div
                key={t.id}
                className={`flex items-center gap-4 rounded-xl border p-4 shadow-card ${
                  checked ? "border-success/30 bg-success/5" : "border-border bg-card"
                }`}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
                  {p?.photo_url ? (
                    <img src={p.photo_url} alt={fullName} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-sm font-semibold">{fullName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-semibold">{fullName}</h3>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                      {t.ticket_types?.name ?? "Bilet"}
                    </span>
                  </div>
                  <div className="mt-0.5 font-mono text-xs text-muted-foreground">{t.code}</div>
                </div>
                <div className="text-right">
                  {checked ? (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-xs font-medium text-success">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {new Date(t.checked_in_at!).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" /> Bekliyor
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function VouchersTab({
  summary,
  totalSold,
  totalRedeemed,
}: {
  summary: { name: string; sold: number; redeemed: number }[];
  totalSold: number;
  totalRedeemed: number;
}) {
  const remaining = totalSold - totalRedeemed;
  return (
    <>
      <div className="mt-6 grid gap-3 md:grid-cols-3">
        <SummaryTile label="Satılan kupon" value={totalSold.toString()} icon={<Wallet className="h-5 w-5" />} tone="default" />
        <SummaryTile label="Kullanılan" value={totalRedeemed.toString()} icon={<CheckCircle2 className="h-5 w-5" />} tone="success" />
        <SummaryTile label="Kalan" value={remaining.toString()} icon={<Clock className="h-5 w-5" />} tone={remaining > 0 ? "warning" : "default"} />
      </div>

      {summary.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          Henüz içecek kuponu satılmadı.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {summary.map((d) => {
            const pct = d.sold === 0 ? 0 : Math.round((d.redeemed / d.sold) * 100);
            return (
              <div key={d.name} className="rounded-xl border border-border bg-card p-4 shadow-card">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Wine className="h-4 w-4 shrink-0 text-primary" />
                    <h3 className="truncate font-semibold">{d.name}</h3>
                  </div>
                  <div className="text-sm tabular-nums">
                    <span className="font-semibold text-success">{d.redeemed}</span>
                    <span className="text-muted-foreground"> / {d.sold}</span>
                  </div>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-success transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                  <span>%{pct} kullanıldı</span>
                  <span>{d.sold - d.redeemed} kalan</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function SummaryTile({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: "default" | "warning" | "success";
}) {
  const cls =
    tone === "warning"
      ? "border-warning/40 bg-warning/10 text-warning"
      : tone === "success"
        ? "border-success/40 bg-success/10 text-success"
        : "border-border bg-card text-foreground";
  return (
    <div className={`rounded-2xl border ${cls} p-5`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-2 text-3xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
